type LSEntry = {
  value: unknown;
  lastAccessed: number;
  ttlSeconds?: number;
};

function isValidEntry( entry: unknown ): entry is LSEntry {
  if ( !entry || typeof entry !== "object" ) {
    return false;
  }
  const e = entry as LSEntry;
  return "value" in e && typeof e.lastAccessed === "number";
}

/**
 * Generic localStorage helper for JSON-encodable values with optional idle TTL.
 */
const LS = {
  store( key: string, value: unknown, ttlSeconds?: number ): boolean {
    try {
      JSON.stringify( value );
    } catch ( error ) {
      console.warn( "LS.store: value is not JSON-encodable:", error );
      return false;
    }

    const entry: LSEntry = {
      value,
      lastAccessed: Date.now(),
    };

    if ( ttlSeconds !== undefined && ttlSeconds !== null ) {
      entry.ttlSeconds = ttlSeconds;
    }

    try {
      localStorage.setItem( key, JSON.stringify( entry ) );
      return true;
    } catch ( error ) {
      console.warn( "LS.store failed:", error );
      return false;
    }
  },

  fetch( key: string ): unknown | null {
    const raw = localStorage.getItem( key );

    if ( raw == null ) {
      return null;
    }

    try {
      const entry = JSON.parse( raw );

      if ( !isValidEntry( entry ) ) {
        localStorage.removeItem( key );
        return null;
      }

      if ( typeof entry.ttlSeconds === "number" ) {
        if ( Date.now() - entry.lastAccessed > entry.ttlSeconds * 1000 ) {
          localStorage.removeItem( key );
          return null;
        }
      }

      entry.lastAccessed = Date.now();
      localStorage.setItem( key, JSON.stringify( entry ) );
      return entry.value;
    } catch {
      localStorage.removeItem( key );
      return null;
    }
  },

  clear( key: string ): void {
    localStorage.removeItem( key );
  },
};

export default LS;
